import React, { useMemo, useState } from 'react';
import styled, { css } from 'styled-components';
import Icon from '@mdi/react';
import { mdiDelete } from '@mdi/js';
import { Button, ConfirmationModal } from '@warehouse/shared/legacy-ui';
import { objectBrowserStoreSelector, useObjectBrowserStore } from '@warehouse/object-browser/domain';
import { NxObjectType } from '@warehouse/object-browser/core';
import { useStateQuery } from '@warehouse/shared/util';
import { useCanDelete } from '@warehouse/auth';
import { useSnackbar } from 'notistack';
import {
	BrokerJobDetails,
	BrokerJobStatus,
	NX_PURGE_SERVICE_UUID,
	brokerJobsRepositorySingleton,
	brokerJobsStoreSelector,
	brokerServicesRepositorySingleton,
	isNxPurgeJobOutput,
	useBrokerJobsStore,
} from '@warehouse/object-browser/feature-broker';

const JOB_POLL_INTERVAL_MS = 1000;
const JOB_POLL_TIMEOUT_MS = 60000;
const FINISHED_STATUSES: BrokerJobStatus[] = ['SUCCEEDED', 'FAILED', 'CANCELLED'];

function sleep(ms: number): Promise<void> {
	return new Promise((resolve) => {
		setTimeout(resolve, ms);
	});
}

async function waitForPurgeToFinish(
	jobId: string,
	deadline: number = Date.now() + JOB_POLL_TIMEOUT_MS,
): Promise<BrokerJobDetails> {
	const job = await brokerJobsRepositorySingleton.get().getJob({ jobId });
	if (FINISHED_STATUSES.includes(job.status) || Date.now() >= deadline) return job;

	await sleep(JOB_POLL_INTERVAL_MS);
	return waitForPurgeToFinish(jobId, deadline);
}

export function DeleteObject() {
	// Nx Purge declares requiredPermissions ["warehouse.delete"] in services.json, so that is
	// the scope the button answers to - not the broader admin one.
	const canDelete = useCanDelete();
	const { enqueueSnackbar } = useSnackbar();
	const nxObjectFull = useObjectBrowserStore(objectBrowserStoreSelector.selectedNxObjectFull);
	const selectedNxObject = useObjectBrowserStore(objectBrowserStoreSelector.selectedNxObject);
	const { refresh } = useBrokerJobsStore(brokerJobsStoreSelector.actions);
	const { closeNxObjectDrawer } = useObjectBrowserStore(objectBrowserStoreSelector.actions);

	const [, setSelectedTab] = useStateQuery<string>({ queryName: 'selectedNxObjectDrawerTab', type: 'string' });
	const [, setSelectedNxObjectUuid] = useStateQuery<string>({ queryName: 'selectedNxObjectUuid', type: 'string' });

	const nxPurgeService = useMemo(
		() =>
			brokerServicesRepositorySingleton
				.get()
				.getAvailableServicesConfig()
				.find((service) => service.serviceUUID === NX_PURGE_SERVICE_UUID),
		[],
	);

	const [confirmDelete, setConfirmDelete] = useState(false);
	const [isPurging, setIsPurging] = useState(false);

	const objectName = nxObjectFull?.nxObject?.name;

	if (!canDelete || selectedNxObject?.itemType !== NxObjectType.File) return null;

	const onClose = () => {
		if (isPurging) return;
		setConfirmDelete(false);
	};

	const onConfirmDelete = async () => {
		if (!nxObjectFull || !nxPurgeService) return;

		const { uuid, name } = nxObjectFull.nxObject;
		setIsPurging(true);

		try {
			const { jobId } = await brokerJobsRepositorySingleton
				.get()
				.submitJob({ nxObjectUuid: uuid, serviceUUID: nxPurgeService.serviceUUID });
			const job = await waitForPurgeToFinish(jobId);
			refresh(uuid);
			setConfirmDelete(false);
			if (job.status !== 'SUCCEEDED') {
				enqueueSnackbar(job.error ?? `${name} was not deleted: the purge ${job.status.toLowerCase()}.`, {
					variant: 'error',
				});
				return;
			}

			const alreadyAbsent = isNxPurgeJobOutput(job.output) && job.output.alreadyAbsent;
			enqueueSnackbar(alreadyAbsent ? `${name} was already deleted.` : `${name} deleted successfully.`, {
				variant: 'success',
			});
			closeNxObjectDrawer();
			setSelectedTab(null);
			setSelectedNxObjectUuid(null);
		} catch {
			setConfirmDelete(false);
			enqueueSnackbar(`${name} was not deleted: the purge could not be run.`, { variant: 'error' });
		} finally {
			setIsPurging(false);
		}
	};

	return (
		<Wrapper>
			<DeleteButton
				aria-label="Delete object"
				nxstyle="secondary-red"
				disabled={!nxObjectFull}
				onClick={() => setConfirmDelete(true)}
			>
				<Icon path={mdiDelete} size="30px" />
			</DeleteButton>
			<ConfirmationModal
				open={confirmDelete}
				onClose={onClose}
				onConfirm={onConfirmDelete}
				title="Delete Object"
				message={`This will permanently delete ${objectName}. This action cannot be undone.`}
				confirmText="Delete"
				variant="danger"
				showButtonIcons={false}
				confirmButtonLoading={isPurging}
				confirmButtonDisabled={isPurging || !nxObjectFull || !nxPurgeService}
			/>
		</Wrapper>
	);
}

const DeleteButton = styled(Button)`
	align-items: center;
	display: flex;
	justify-content: center;
	line-height: 0;

	svg {
		display: block;
	}
`;

const Wrapper = styled.div(
	({ theme }) => css`
		display: flex;
		justify-content: flex-end;
		margin-top: auto;
		padding: ${theme.spacing(1)};
	`,
);
